// Fixture opportunity + pricing snapshot for Manager → Proposals PDF preview.
// Uses sample numbers so calc lines / payment milestones render; text and
// package selection come from the live org presets for (formatKey, productType).

import type {
  Opportunity,
  OpportunityPricingSnapshot,
} from "@/types/opportunity";
import { computePricingTotals, emptyPricingInputs } from "@/lib/opportunity/calc";
import { resolveOpportunityFormatKey } from "@/lib/opportunity/formatRegistry";
import type { GenerateProposalArgs } from "@/components/opportunity/pdf/generateProposalPdf";
import type { OrgProposalContactRow } from "@/lib/opportunity/proposalContactTypes";
import type { AuthProfile } from "@/hooks/authStateModel";
import type { OpportunityDocumentArchetype } from "@/lib/opportunity/documentArchetypes";

const PREVIEW_SUBTOTAL = 100_000;
const PREVIEW_MARGIN_PCT = 10;
const PREVIEW_COMMISSION_PCT = 5;
const PREVIEW_DEPOSIT_PCT = 25;

export function buildManagerProposalPreviewOpportunity(args: {
  productType: string;
  organizationId?: string;
}): Opportunity {
  const now = new Date().toISOString();
  return {
    id: "00000000-0000-4000-8000-000000000001",
    organization_id: args.organizationId ?? "",
    lead_id: null,
    location_id: null,
    assigned_user_id: null,
    product_type: args.productType,
    status: "quoting",
    project_name: "Sample Project",
    customer_name: "Sample Customer",
    customer_company: "Sample Company",
    customer_phone: "(555) 010-0000",
    customer_email: "sample@example.com",
    customer_address: "123 Sample St",
    use_rep_proposal_contact: false,
    active_revision_id: null,
    created_by_user_id: null,
    external_ghl_opportunity_id: null,
    external_ghl_contact_id: null,
    crm_pushed_at: null,
    created_at: now,
    updated_at: now,
    archived_at: null,
    scheduling_notes: "",
  };
}

export function buildManagerProposalPreviewSnapshot(args: {
  opportunityId: string;
  organizationId?: string;
  productType: string;
}): OpportunityPricingSnapshot {
  const format_key = resolveOpportunityFormatKey(args.productType);
  const inputs = {
    ...emptyPricingInputs({
      format_key,
      margin_pct: PREVIEW_MARGIN_PCT,
      commission_pct: PREVIEW_COMMISSION_PCT,
      deposit_pct: PREVIEW_DEPOSIT_PCT,
    }),
    subtotal_input: PREVIEW_SUBTOTAL,
  };
  const totals = computePricingTotals(inputs);
  const now = new Date().toISOString();
  return {
    ...inputs,
    ...totals,
    id: "00000000-0000-4000-8000-000000000002",
    organization_id: args.organizationId ?? "",
    opportunity_id: args.opportunityId,
    revision_number: 1,
    created_by_user_id: null,
    created_at: now,
  };
}

export type ManagerProposalPreviewRenderArgs = {
  productType: string;
  alternateKey: string | null;
  /** When set, renders a single library page instead of a package. */
  previewPageId?: string | null;
  archetype?: OpportunityDocumentArchetype;
  brandName: string;
  organizationName: string;
  tagline: string | null;
  logoUrl: string | null;
  proposalContactRow: OrgProposalContactRow;
  profile: AuthProfile | null;
  organizationId?: string;
};

/** Build `GenerateProposalArgs` for manager-side fixture preview. */
export function buildManagerProposalPreviewRenderArgs(
  args: ManagerProposalPreviewRenderArgs,
): GenerateProposalArgs {
  const opportunity = buildManagerProposalPreviewOpportunity({
    productType: args.productType,
    organizationId: args.organizationId,
  });
  const snapshot = buildManagerProposalPreviewSnapshot({
    opportunityId: opportunity.id,
    organizationId: args.organizationId,
    productType: args.productType,
  });
  return {
    opportunity,
    snapshot,
    revision: null,
    brandName: args.brandName,
    organizationName: args.organizationName,
    tagline: args.tagline,
    logoUrl: args.logoUrl,
    proposalContactRow: args.proposalContactRow,
    profile: args.profile,
    activeLocation: null,
    alternateKey: args.alternateKey,
    previewPageId: args.previewPageId ?? null,
    archetype: args.archetype ?? "proposal",
  };
}
